import React from 'react';
import {
  // convertCronExpressionToTimeAndDays,
  // convertDaysToLabels,
  // convertISODateToMilestoneDate,
  formatMilestoneDateToString,
  getMilestoneActiveTime,
  getMilestoneActiveTimeDaysUntilEnd,
  getMilestoneProgress,
  getMilestoneStatus,
  term,
} from 'utils/project-ruleset';
import { Body, Colors, Overline, Title, Tooltip } from '@cognite/cogs.js';
import { ProgressBar, VerticalDivider } from 'components';
import { Milestone } from 'types/dt-graphql-apollo-v2';
import styled from 'styled-components/macro';

// export const MilestoneRuns = ({
//   selectedMilestone,
// }: {
//   selectedMilestone: Milestone | null;
// }) => {
//   const hasActiveTime = !!selectedMilestone?.endActiveTime;

//   const startScheduleTime =
//     selectedMilestone &&
//     !!selectedMilestone?.startScheduleTime &&
//     convertISODateToMilestoneDate(selectedMilestone?.startScheduleTime);

//   const endScheduleTime =
//     selectedMilestone &&
//     !!selectedMilestone?.endScheduleTime &&
//     convertISODateToMilestoneDate(selectedMilestone?.endScheduleTime);

//   const schedule =
//     selectedMilestone &&
//     !!selectedMilestone?.schedule &&
//     convertCronExpressionToTimeAndDays(selectedMilestone?.schedule);
//   const scheduleDays =
//     schedule && schedule?.days && convertDaysToLabels(schedule?.days);

//   const scheduleTime = schedule && schedule?.time;
//   return (
//     <div>
//       <StyledOverline level={1}>RUNS</StyledOverline>

//       <StyledBodyGroup level={2}>
//         {scheduleDays && scheduleDays.length > 0 && (
//           <span>
//             {scheduleDays.length === 1 ? 'Day: ' : 'Days: '}
//             <StyledInlineFlex>
//               {scheduleDays.length === 7
//                 ? 'Every day'
//                 : scheduleDays.join(', ')}
//             </StyledInlineFlex>
//           </span>
//         )}
//       </StyledBodyGroup>
//       <StyledBodyGroup level={2}>
//         {scheduleTime && <span>Time: {scheduleTime}</span>}
//         {scheduleTime && startScheduleTime && endScheduleTime && (
//           <VerticalDivider />
//         )}
//         <span>
//           {startScheduleTime && endScheduleTime
//             ? `${startScheduleTime} to ${endScheduleTime}`
//             : hasActiveTime && `Runs during active ${term.milestone}`}
//         </span>
//       </StyledBodyGroup>
//     </div>
//   );
// };

type MilestoneStatusColumnProps = {
  milestones: Milestone[];
  selectedMilestone?: Milestone;
};

export const MilestoneStatusColumn = ({
  milestones,
  selectedMilestone,
}: MilestoneStatusColumnProps) => {
  const status = getMilestoneStatus(milestones, selectedMilestone);
  const progress = getMilestoneProgress(milestones, selectedMilestone);
  const daysLeft = getMilestoneActiveTimeDaysUntilEnd(
    milestones,
    selectedMilestone
  );
  const activeTime =
    selectedMilestone && getMilestoneActiveTime(selectedMilestone, milestones);

  const startActiveTime =
    !!activeTime?.start && formatMilestoneDateToString(activeTime.start);

  const endActiveTime =
    !!activeTime?.end && formatMilestoneDateToString(activeTime.end);

  return (
    <StyledCol>
      <StyledInlineFlex>
        <Title level={5}>Status</Title>
        {status && <Body>{status}</Body>}
      </StyledInlineFlex>

      {startActiveTime && endActiveTime && (
        <div>
          <StyledOverline level={2}>ACTIVE TIME</StyledOverline>
          <Body level={2}>{`${startActiveTime} to ${endActiveTime}`}</Body>
        </div>
      )}
      {progress !== null && (
        <Tooltip content={`${progress}% complete`}>
          <ProgressBar
            trackColor={'var(--cogs-primary)'}
            baseColor={'var(--cogs-greyscale-grey2)'}
            value={progress}
          />
        </Tooltip>
      )}
      {!!daysLeft && (
        <Body level={2}>
          <StyledSpanBold>{daysLeft}</StyledSpanBold> days left
        </Body>
      )}
    </StyledCol>
  );
};

type MilestoneInfoProps = {
  milestones: Milestone[];
  rulesCount: number;
  rulesetsCount: number;
  selectedMilestone?: Milestone;
  show: boolean;
};

export const MilestoneInfo = ({
  milestones,
  rulesCount,
  rulesetsCount,
  selectedMilestone,
  show,
}: MilestoneInfoProps) => (
  <StyledCollapsibleMilestoneInfo expanded={show}>
    <StyledGrid>
      <StyledCol>
        <Title level={5}>General info</Title>
        <div>
          <StyledOverline level={1}>NAME</StyledOverline>
          <Body level={2}>{selectedMilestone?.name}</Body>
        </div>
        <div>
          <StyledOverline level={1}>DESCRIPTION</StyledOverline>
          <Body level={2}>{selectedMilestone?.description}</Body>
        </div>
        {/* <MilestoneRuns selectedMilestone={selectedMilestone} /> */}
      </StyledCol>
      <StyledCol>
        <Title level={5}>Content</Title>
        <div>
          <StyledInlineFlex>
            <StyledBodyGroup level={2}>
              <StyledSpanBold>{rulesetsCount}</StyledSpanBold>{' '}
              {rulesetsCount !== 1 ? term.rulesets : term.ruleset}
              <VerticalDivider />
              <StyledSpanBold>{rulesCount}</StyledSpanBold>{' '}
              {rulesCount !== 1 ? term.rules : term.rule}
            </StyledBodyGroup>
          </StyledInlineFlex>
        </div>
      </StyledCol>
      <MilestoneStatusColumn
        selectedMilestone={selectedMilestone}
        milestones={milestones}
      />
    </StyledGrid>
  </StyledCollapsibleMilestoneInfo>
);

const StyledGrid = styled.div`
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
  background: ${Colors['decorative--grayscale--white']};
  width: 100%;
  padding: 1rem 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const StyledCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const StyledCollapsibleMilestoneInfo = styled.div<{
  expanded: boolean;
}>`
  ${({ expanded }) => !expanded && 'overflow: hidden;'};
  max-height: ${({ expanded }) => (expanded ? 'none' : 0)};
  width: 100%;
  margin-bottom: 0;
  margin-top: 0;
  ${StyledCol} {
    gap: 1.5rem;
  }
`;

const StyledOverline = styled(Overline)`
  /* Replaced Colors token with CSS var for COGS v9 */
  color: var(--cogs-text-secondary);
  text-transform: uppercase;
`;

const StyledInlineFlex = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 1rem;
`;

const StyledBodyGroup = styled(Body)`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledSpanBold = styled.span`
  font-weight: 700;
`;
